import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Tag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function RebookOffer() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/rebooking/offer/${token}`).then((r) => setOffer(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "This offer link is invalid or has expired."));
  }, [token]);

  const decide = async (accept) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/rebooking/offer/${token}`, { accept });
      setDone(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit."); }
    finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen bg-black text-white flex items-center justify-center p-6"><p className="text-white/60">{err}</p></div>;
  if (!offer) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-[#F2B705]" /></div>;

  return (
    <div className="min-h-screen bg-black text-white py-12 px-4">
      <div className="max-w-md mx-auto space-y-5">
        <h1 className="font-display font-black text-2xl flex items-center gap-2"><Tag className="text-[#F2B705]" /> Your AutoHire Rental Offer</h1>
        <div className="rounded-xl border border-[#F2B705]/40 bg-[#0C0C0C] p-5 space-y-2 text-sm" data-testid="rebook-offer-card">
          <p className="text-white/50">Hi {offer.name?.split(" ")[0]}, AutoHire has prepared this offer for you:</p>
          <div className="text-lg font-bold">{offer.vehicle}</div>
          <div className="text-white/70">{offer.pickup_date} → {offer.return_date}</div>
          {offer.package && <div className="text-white/50 text-xs">Package: {offer.package}</div>}
          <div className="text-[#F2B705] font-black text-2xl">{money(offer.total)}</div>
          {offer.notes && <div className="text-white/45 text-xs">{offer.notes}</div>}
        </div>
        {done ? (
          <div data-testid="rebook-offer-done" className="rounded-xl border border-emerald-500/40 bg-emerald-500/[0.06] p-6 text-center space-y-2">
            <CheckCircle2 className="mx-auto text-emerald-400" size={34} />
            <p className="text-white/85 text-sm">{done.accepted
              ? "Offer accepted! Your booking is being prepared — watch your phone/email for the payment link and rental agreement."
              : "No problem — we've let AutoHire know you declined this offer."}</p>
            {done.accepted && done.pay_url && <a href={done.pay_url} className="text-[#F2B705] text-sm font-bold hover:underline">Pay now →</a>}
          </div>
        ) : offer.status !== "Offer Sent" ? (
          <p className="text-white/50 text-sm">This offer was already {offer.status.toLowerCase()}.</p>
        ) : (
          <div className="flex gap-3">
            <button data-testid="offer-accept" onClick={() => decide(true)} disabled={busy}
              className="flex-1 bg-[#F2B705] text-black font-bold py-3 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={15} /> : <CheckCircle2 size={15} />} Accept Offer
            </button>
            <button data-testid="offer-decline" onClick={() => decide(false)} disabled={busy}
              className="flex-1 border border-white/20 text-white/70 font-bold py-3 rounded-md hover:bg-white/5 flex items-center justify-center gap-1.5 disabled:opacity-60">
              <XCircle size={15} /> Decline
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
